// src/components/verification/EmailVerification.js
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { apiFetch } from '../../utils/apiClient';
import { BASE_URL } from '../../utils/config';
import { validateEmail } from '../../utils/userValidation';
import './EmailVerification.css';

const EmailVerification = () => {
  const { user, updateUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    if (user?.email) {
      setEmailInput(user.email);
    }
  }, [user.email]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEmailInput(user.email); // Reset to original email
  };

  const handleSaveEmail = async () => {
    const emailError = validateEmail(emailInput);
    if (emailError) {
      addNotification(emailError, 'error');
      return;
    }

    if (emailInput.toLowerCase() === user.email.toLowerCase()) {
      addNotification(t('emailVerification.notifications.alreadyCurrentEmail'), 'info');
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiFetch(
        `${BASE_URL}/authUser/email`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailInput }),
        },
        user.token
      );

      const data = await response.json();

      if (response.ok) {
        addNotification(data.message, 'success');
        // The backend response includes the updated user fields
        updateUser(data.user);
        setIsEditing(false);
      } else {
        throw new Error(data.message || data.error || t('emailVerification.notifications.updateFailed'));
      }
    } catch (error) {
      addNotification(t('emailVerification.notifications.genericError', { error: error.message }), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    addNotification(t('emailVerification.notifications.sendingTo', { email: user.email }), 'info');

    try {
      const res = await fetch(`${BASE_URL}/authUser/request-verification-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      const resData = await res.json();
      if (res.ok) {
        addNotification(t('emailVerification.notifications.sentTo', { email: user.email }), 'success', 5000);
      } else {
        throw new Error(resData.message || 'Failed to send verification email.');
      }
    } catch (err) {
      addNotification(t('emailVerification.notifications.genericError', { error: err.message }), 'error', 5000);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="email-verification-container">
      <h4>{user.isEmailVerified && !isEditing ? t('emailVerification.verifiedTitle') : t('emailVerification.unverifiedTitle')}</h4>

      {user.isEmailVerified && !isEditing ? (
        <>
          <p>{t('emailVerification.verifiedDescription')}</p>
          <div className="email-display">
            <input type="email" value={user.email || ''} disabled className="email-input-field" autoComplete="email" />
            <button onClick={handleEditClick} className="change-btn">
              {t('emailVerification.buttonChange')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p>{t('emailVerification.unverifiedDescription')}</p>

          {isEditing ? (
            <div className="email-edit-form">
              <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} className="email-input-field" autoFocus autoComplete="email" />
              <div className="email-edit-actions">
                <button onClick={handleCancelClick} className="btn-secondary" disabled={isLoading}>
                  {t('emailVerification.buttonCancel')}
                </button>
                <button onClick={handleSaveEmail} className="btn-primary" disabled={isLoading}>
                  {isLoading ? t('emailVerification.buttonSaving') : t('emailVerification.buttonSaveEmail')}
                </button>
              </div>
            </div>
          ) : (
            <div className="email-display">
              <input type="email" value={user.email || ''} disabled className="email-input-field" autoComplete="email" />
              <button onClick={handleEditClick} className="change-btn standout">
                {t('emailVerification.buttonChange')}
              </button>
            </div>
          )}

          {!user.isEmailVerified && !isEditing && (
            <div className="unverified-notice">
              <p>{t('emailVerification.checkInbox')}</p>
              <div className="unverified-actions">
                <button onClick={handleResendVerification} disabled={isResending} className="btn-primary">
                  {isResending ? t('emailVerification.buttonSending') : t('emailVerification.buttonResend')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailVerification;
